import { jsonSchemaToZod } from "./schema-converter.js";
import type { SdkFunctionTool } from "./library-tool-sdk.js";

export interface LibraryToolDefinition {
  toolId: string;
  description: string;
  module: string;
  exportName: string;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
}

export interface LibraryToolRegistry {
  register(def: LibraryToolDefinition): void;
  get(toolId: string): LibraryToolDefinition | undefined;
  getAll(): LibraryToolDefinition[];
  execute(toolId: string, input: unknown): Promise<unknown>;
}

export function createLibraryToolRegistry(): LibraryToolRegistry {
  const definitions = new Map<string, LibraryToolDefinition>();
  const moduleCache = new Map<string, Record<string, unknown>>();

  return {
    register(def: LibraryToolDefinition): void {
      definitions.set(def.toolId, def);
    },

    get(toolId: string): LibraryToolDefinition | undefined {
      return definitions.get(toolId);
    },

    getAll(): LibraryToolDefinition[] {
      return [...definitions.values()];
    },

    async execute(toolId: string, input: unknown): Promise<unknown> {
      const def = definitions.get(toolId);
      if (!def) {
        throw new Error(`Library tool not found: ${toolId}`);
      }

      if (def.inputSchema) {
        const schema = jsonSchemaToZod(def.inputSchema);
        const result = schema.safeParse(input);
        if (!result.success) {
          throw new Error(
            `Input validation failed for tool "${toolId}": ${result.error.message}`,
          );
        }
        input = result.data;
      }

      let mod = moduleCache.get(def.module);
      if (!mod) {
        try {
          mod = (await import(def.module)) as Record<string, unknown>;
          moduleCache.set(def.module, mod);
        } catch (error) {
          const message =
            error instanceof Error ? error.message : String(error);
          throw new Error(
            `Failed to import module "${def.module}" for tool "${toolId}": ${message}`,
          );
        }
      }

      const fn = mod[def.exportName];
      if (typeof fn !== "function") {
        throw new Error(
          `Export "${def.exportName}" not found in module "${def.module}" for tool "${toolId}"`,
        );
      }

      const output = await (fn as (input: unknown) => Promise<unknown>)(input);

      if (def.outputSchema) {
        const schema = jsonSchemaToZod(def.outputSchema);
        const result = schema.safeParse(output);
        if (!result.success) {
          throw new Error(
            `Output validation failed for tool "${toolId}": ${result.error.message}`,
          );
        }
      }

      return output;
    },
  };
}

/**
 * Create a LibraryToolRegistry backed by pre-built SdkFunctionTool handlers.
 * Unlike createLibraryToolRegistry (which resolves tools via dynamic import),
 * this variant delegates execute() directly to the tool's in-process handler.
 */
export function createRegistryFromSdkTools(
  tools: SdkFunctionTool[],
): LibraryToolRegistry {
  const handlers = new Map<string, SdkFunctionTool>();
  const definitions = new Map<string, LibraryToolDefinition>();

  for (const tool of tools) {
    handlers.set(tool.name, tool);
    definitions.set(tool.name, {
      toolId: tool.name,
      description: tool.description,
      module: "__inline__",
      exportName: "__handler__",
      inputSchema: tool.inputSchema,
    });
  }

  return {
    register(def: LibraryToolDefinition): void {
      definitions.set(def.toolId, def);
    },

    get(toolId: string): LibraryToolDefinition | undefined {
      return definitions.get(toolId);
    },

    getAll(): LibraryToolDefinition[] {
      return [...definitions.values()];
    },

    async execute(toolId: string, input: unknown): Promise<unknown> {
      const handler = handlers.get(toolId);
      if (handler) {
        return handler.handler((input ?? {}) as Record<string, unknown>);
      }
      throw new Error(`Library tool not found: ${toolId}`);
    },
  };
}
